import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  Button,
  Grid,
  Typography,
  Card,
  CardContent,
  CardActions,
  Avatar,
  IconButton,
  Box,
  Chip,
  Divider,
  Badge,
} from "@material-ui/core";
import EventIcon from "@material-ui/icons/Event";
import AccessAlarmIcon from "@material-ui/icons/AccessAlarm";
import DescriptionIcon from "@material-ui/icons/Description";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import StarIcon from "@material-ui/icons/Star";
import VerifiedUserIcon from "@material-ui/icons/VerifiedUser";
import FavoriteIcon from "@material-ui/icons/Favorite";
import ChatIcon from "@material-ui/icons/Chat";
import BusinessIcon from "@material-ui/icons/Business";
import useBDocTranslator from "../../hooks/useBDocTranslator";
import ProfileLogo from "../../../images/profile-2.svg";
import { makeStyles } from "@material-ui/core/styles";

//flags images
import NICFlag from "../../../images/nicaragua-flag.png";
import CRCFlag from "../../../images/cr-flg.png";
import BahFlag from "../../../images/bahamas-flag.png";
import USAFlag from "../../../images/usa-flag.png";
import JAMFlag from "../../../images/jamaica-flag.png";
import { renderPlans } from "../../utils/RenderPlans";

const useStyles = makeStyles((theme) => ({
  card: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    borderRadius: theme.spacing(2),
    background: "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
    transition: "all 0.3s ease",
    position: "relative",
    overflow: "hidden",
    border: "1px solid rgba(0, 0, 0, 0.05)",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 8px 20px rgba(0, 0, 0, 0.12)",
    },
  },
  cardContent: {
    flexGrow: 1,
    padding: theme.spacing(2.5),
    position: "relative",
  },
  cardActions: {
    padding: theme.spacing(1.5, 2.5, 2.5, 2.5),
    background: "#f8f9fa",
    borderTop: "1px solid rgba(0, 0, 0, 0.05)",
  },
  buttonContainer: {
    display: "flex",
    width: "100%",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1.5),
    },
  },
  headerSection: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    position: "relative",
  },
  avatarContainer: {
    position: "relative",
    marginRight: theme.spacing(2),
  },
  largeAvatar: {
    width: theme.spacing(11),
    height: theme.spacing(11),
    border: "3px solid #fff",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
  },
  countryFlag: {
    position: "absolute",
    bottom: -2,
    right: -2,
    width: 20,
    height: 20,
    borderRadius: "50%",
    border: "2px solid #fff",
    objectFit: "cover",
    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.15)",
  },
  providerInfo: {
    flex: 1,
    minWidth: 0,
  },
  providerName: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
    color: "#2c3e50",
    fontSize: "1rem",
    lineHeight: 1.3,
  },
  ratingContainer: {
    display: "flex",
    alignItems: "center",
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
  },
  ratingText: {
    marginLeft: theme.spacing(0.5),
    fontSize: "0.8rem",
    fontWeight: 500,
    color: "#f57c00",
  },
  specialtyChip: {
    fontSize: "0.75rem",
    height: 24,
    fontWeight: 500,
    backgroundColor: "#e3f2fd",
    color: "#1976d2",
    "& .MuiChip-label": {
      padding: "0 8px",
    },
  },
  summarySection: {
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    backgroundColor: "#f8f9fa",
    borderRadius: theme.spacing(1),
    border: "1px solid #e9ecef",
  },
  summaryText: {
    color: "#6c757d",
    lineHeight: 1.5,
    fontSize: "0.85rem",
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  availabilityBadge: {
    backgroundColor: "#4caf50",
    color: "white",
    fontSize: "0.7rem",
    fontWeight: 600,
    padding: "4px 8px",
    borderRadius: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
  plansContainer: {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: "#f8f9fa",
    borderRadius: theme.spacing(1),
    border: "1px solid #e9ecef",
  },
  plansTitle: {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#495057",
    marginBottom: theme.spacing(0.5),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  planChip: {
    fontSize: "0.75rem",
    height: 24,
    fontWeight: 500,
    backgroundColor: "#e3f2fd",
    color: "#1976d2",
    borderRadius: theme.spacing(1),
    margin: theme.spacing(0.25),
  },
  actionButton: {
    borderRadius: theme.spacing(1.5),
    textTransform: "none",
    fontWeight: 600,
    padding: theme.spacing(0.8, 1.2),
    fontSize: "0.8rem",
    flex: 1,
    minWidth: 0,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  primaryButton: {
    backgroundColor: "#1976d2",
    color: "white",
    "&:hover": {
      backgroundColor: "#1565c0",
    },
  },
  secondaryButton: {
    color: "#1976d2",
    borderColor: "#1976d2",
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: "rgba(25, 118, 210, 0.04)",
    },
  },
  verifiedBadge: {
    position: "absolute",
    top: theme.spacing(1),
    left: theme.spacing(1),
    backgroundColor: "#4caf50",
    color: "white",
    padding: "3px 8px",
    borderRadius: theme.spacing(1),
    fontSize: "0.65rem",
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    zIndex: 2,
  },
}));

export default function ProviderCard(props) {
  const classes = useStyles();
  let history = useHistory();
  const { t, changeDirectLanguage } = useBDocTranslator();

  // Initialize language translations
  useEffect(() => {
    changeDirectLanguage();
  }, []);

  const handleClick = () => {
    history.push({
      pathname: "/patient/appointments/create",
      state: {
        providerId: props.providerId,
        providerUserId: props.providerUserId,
        locations: props.locations,
      },
    });
  };

  const handleClickDetails = () => {
    history.push({
      pathname: `provider/details/${props.id}`,
      state: props.profileImage,
    });
  };

  const handleClickTextConsultation = () => {
    history.push({
      pathname: `/patient/textconsultation`,
      state: { providerId: props.providerId },
    });
  };

  const getCountryFlag = (country) => {
    switch (country) {
      case "CRC":
        return CRCFlag;
      case "USA":
        return USAFlag;
      case "NIC":
        return NICFlag;
      case "TBH":
        return BahFlag;
      case "JAM":
        return JAMFlag;
      default:
        return CRCFlag;
    }
  };

  const renderStars = (rating) => {
    const stars = [];
    const maxRating = 5;
    const currentRating = rating || 0;

    for (let i = 1; i <= maxRating; i++) {
      stars.push(
        <StarIcon
          key={i}
          style={{
            color: i <= currentRating ? "#ffc107" : "#e0e0e0",
            fontSize: "1rem",
          }}
        />
      );
    }
    return stars;
  };

  return (
    <Card className={classes.card}>
      {/* Verified Badge */}
      <Box className={classes.verifiedBadge}>
        <VerifiedUserIcon style={{ fontSize: 10 }} />
        Verified
      </Box>

      {/* Availability Badge */}
      {props?.available && (
        <Box className={classes.availabilityBadge}>
          <AccessAlarmIcon style={{ fontSize: 10 }} />
          {t("Available today")}
        </Box>
      )}

      <CardContent className={classes.cardContent}>
        {/* Header Section */}
        <Box className={classes.headerSection}>
          <Box className={classes.avatarContainer}>
            <Avatar
              alt={props.fullname}
              src={props.profileImage || ProfileLogo}
              className={classes.largeAvatar}
            />
            {props.country && (
              <img
                src={getCountryFlag(props.country)}
                alt="Country flag"
                className={classes.countryFlag}
              />
            )}
          </Box>

          <Box className={classes.providerInfo}>
            <Typography variant="h6" className={classes.providerName}>
              {props?.title &&
                t(
                  props?.title.slice(0, 1) + props?.title.slice(1).toLowerCase()
                )}{" "}
              {props.fullname || "Invalid User"}
            </Typography>

            {/* Specialty */}
            {props.specialty && props.specialty[0] && (
              <Chip
                label={t(props.specialty[0])}
                size="small"
                className={classes.specialtyChip}
              />
            )}

            {/* Rating */}
            {props.rating > 0 && (
              <Box className={classes.ratingContainer}>
                {renderStars(props.rating)}
                <Typography className={classes.ratingText}>
                  {props.rating}/5
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Plans */}
        {props?.plans && props.plans.length > 0 && (
          <Box className={classes.plansContainer}>
            <Typography variant="body2" className={classes.plansTitle}>
              <BusinessIcon style={{ fontSize: 16 }} />
              {t("My Plans")}
            </Typography>
            <Box style={{ display: "flex", flexWrap: "wrap" }}>
              {props.plans.slice(0, 2).map((plan, index) => (
                <Chip
                  key={index}
                  label={plan.plan?.name || "Premium"}
                  size="small"
                  className={classes.planChip}
                />
              ))}
              {props.plans.length > 2 && (
                <Chip
                  label={`+${props.plans.length - 2} more`}
                  size="small"
                  className={classes.planChip}
                />
              )}
            </Box>
          </Box>
        )}

        {/* Summary */}
        <Box className={classes.summarySection}>
          <Typography variant="body2" className={classes.summaryText}>
            {props.summary || ""}
          </Typography>
        </Box>
      </CardContent>

      <CardActions className={classes.cardActions}>
        <Box className={classes.buttonContainer}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DescriptionIcon style={{ fontSize: 16 }} />}
            onClick={handleClickDetails}
            className={`${classes.actionButton} ${classes.secondaryButton}`}
          >
            {t("Details")}
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<EventIcon style={{ fontSize: 16 }} />}
            onClick={handleClick}
            className={`${classes.actionButton} ${classes.primaryButton}`}
          >
            {t("Schedule Appointment")}
          </Button>
        </Box>
      </CardActions>
    </Card>
  );
}
